// データ（getcategory.php・パーツの切り分け）（2026-10-21 に app.js から分けた。2026-10-23 に中身へ）
import { C, catByKey, partsLists } from './model.js';
import { DIR, NS } from '../config.js';
import { keyOf } from './util.js';
import { loadInflate } from './zlib.js';
import type { Category, Num, Part, SaveData } from '../types.js';

// 文字列を DOM にするときの入れもの（使い回す）
let parseBox: SVGElement | null = null, decodeBox: HTMLTextAreaElement | null = null;
// まだ読んでいないカテゴリのパーツ（キャラのプリセットなど）は、手が空いたときに読んでおく（はじめて開いたときに止まらないように）
// 1カテゴリを読むあいだは画面が止まる（大きいカテゴリは CPU を 4 倍遅くした端末で 70ms ほど）ので、1つずつ・触ってから 1 秒は読まない
// （押したときに先読みと重なって待たされないように。2026-10-18）
let lastInputAt = 0;
// 画面から「今さわられた」と知らせる（画面が出た直後など。先読みを少し待たせる）
export function markInput() { lastInputAt = performance.now(); }

// ---- データ ----
// getcategory.php の中身。index.php が版の番号付きの URL（GET。ブラウザが覚えるので2回目からは読まない）を CREATE_DATA_URL に入れ、
// <head> で先読みしている。番号が無いとき（モックなど）は前と同じ POST
export const DATA_URL = window.CREATE_DATA_URL || '';
export function loadData() {
  const req = DATA_URL ? fetch(DATA_URL) : fetch('/' + DIR + '/create/getcategory.php', {
    method: 'POST',
    headers: { 'X-Requested-With': 'XMLHttpRequest', 'Content-Type': 'application/x-www-form-urlencoded' },
    body: 'key = dataload&comp = true' // エンジンと同じ書き方（PHP 側では key_ = " dataload"）
  });
  return req.then((res) => {
    if (!res.ok) throw new Error('HTTP ' + res.status);
    return res.json();
  }).then((json) => {
    C.data = json;
    const packed = C.data.CACHE;
    C.data.CACHE = null;
    return inflateParts(packed as string);
  }).then(later).then((text) => {
    C.partsCache = JSON.parse(text);
    C.cats = C.data.CATEGORY;
    C.cats.forEach((c) => { catByKey[keyOf(c)] = c; });
    C.switchRoot = new DOMParser().parseFromString(C.data.SWITCH, 'text/html').querySelector('svg');
  });
}
// パーツ（CACHE）: base64 → zlib 展開 → JSON の文字列（chibi.setCategory と同じ中身）。
// 使えればブラウザの機能（data: URL の fetch と DecompressionStream）で。JS の Zlib.Inflate は CPU を 4 倍遅くした端末で
// 約 0.4 秒画面を止め、その間のタップが待たされていた（2026-10-18）。古いブラウザ（Safari 16.3 まで など）は前と同じ JS で（Zlib.js はそのときだけ読む。2026-10-21〜）。
// 文字はブラウザの方は UTF-8、JS の方は1バイト1文字で読むが、CHOCO のパーツは ASCII だけなのでどちらも同じになる
export function inflateParts(packed: string): Promise<string> {
  const js = () => {
    const raw = atob(packed), bytes = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
    const buf = new window.Zlib!.Inflate(bytes).decompress(), chunks: string[] = [];
    for (let p = 0; p < buf.length; p += 8192) chunks.push(String.fromCharCode.apply(null, buf.subarray(p, p + 8192) as unknown as number[]));
    return chunks.join('');
  };
  if (!window.DecompressionStream || !window.Response || !window.TextDecoder) return later().then(loadInflate).then(js);
  // base64 → バイト列はブラウザに（data: URL の fetch）。展開には 64KB ずつ渡し、8ms 続けたら次のタスクへ回す（2026-10-18）。
  // 丸ごと渡すと展開そのものが1つの長いタスク（CPU 4 倍で 80ms ほど）になり、データがもう手元にあるとマイクロタスクのまま
  // 0.4 秒ほど続くこともあった（本番の Lighthouse で見つけた）。区切りを回数で決めると、待ちが積み重なって画面ができるのが遅れた
  return fetch('data:application/octet-stream;base64,' + packed).then((res) => res.arrayBuffer()).then((buf) => {
    const bytes = new Uint8Array(buf), ds = new DecompressionStream('deflate');
    const writer = ds.writable.getWriter(), reader = ds.readable.getReader();
    let decoder = new TextDecoder(), out: string[] = [], off = 0, slice = performance.now(), CHUNK = 65536;
    const write = (): Promise<void> => {
      if (off >= bytes.length) return writer.close();
      const piece = bytes.subarray(off, off + CHUNK);
      off += CHUNK;
      return writer.write(piece).then(() => {
        if (performance.now() - slice < 8) return write();
        return later().then(() => { slice = performance.now(); return write(); });
      });
    };
    const read = (): Promise<void> => {
      return reader.read().then((r) => {
        if (r.done) return;
        out.push(decoder.decode(r.value, { stream: true }));
        return read();
      });
    };
    return Promise.all([write(), read()]).then(() => { out.push(decoder.decode()); return out.join(''); });
  }).catch(js);
}
// 次のタスクで続ける（重い処理を1つの長いタスクにまとめない。長いと、その間のタップが待たされる）
export function later<T = undefined>(value?: T): Promise<T> {
  return new Promise<T>((resolve) => { setTimeout(() => { resolve(value as T); }, 0); });
}
// パーツの SVG の直下の g を、パーツ番号ごとに x / a / y の層へまとめる（chibi.getPartsLayerList と同じ）。
// カテゴリの SVG は丸ごと DOM にせず、文字列のまま直下の要素ごとに切り分け、層（part.x など）は初めて使うときにその部分だけ DOM にする。
// 丸ごとだと大きいカテゴリ（髪など 1.6MB）で CPU を 4 倍遅くした端末で 70ms ほど止まり、その間のタップが待たされていた。
// 切り分けだけなら 1/7 ほど（2026-10-18）。結果は丸ごと読んだときと同じ（CHOCO の 35 カテゴリ 3,268 要素で outerHTML がすべて一致。
// ほかのメーカーに広げるときは tools/chibi-unify/check-parts-split.mjs で確かめる）
export const TOP_TAG = /<(\/?)([a-zA-Z][\w:.-]*)\b[^>]*?(\/?)>|<!--[\s\S]*?-->|<!\[CDATA\[[\s\S]*?\]\]>|<\?[\s\S]*?\?>/g;
export const ID_ATTR = /\sid\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+))/i;
// 一番外の <svg> の直下の要素を、文字列のまま切り出す
export function splitTop(s: string) {
  const items: string[] = [];
  if (s.indexOf('<svg') < 0) return items;
  let open = s.indexOf('>', s.indexOf('<svg')) + 1, close = s.lastIndexOf('</svg>'), depth = 0, start = -1, m;
  TOP_TAG.lastIndex = open;
  while ((m = TOP_TAG.exec(s)) && m.index < close) {
    if (!m[2]) continue; // コメントなど
    if (m[1]) { depth--; if (depth === 0) items.push(s.slice(start, TOP_TAG.lastIndex)); }
    else if (m[3]) { if (depth === 0) items.push(s.slice(m.index, TOP_TAG.lastIndex)); }
    else { if (depth === 0) start = m.index; depth++; }
  }
  return items;
}
// 切り出した要素の id（DOM の g.id と同じ。&...; があれば文字に戻す）
export function topId(item: string) {
  const m = ID_ATTR.exec(item.slice(0, item.indexOf('>') + 1));
  let id = m ? (m[1] != null ? m[1] : m[2] != null ? m[2] : m[3]) : '';
  if (id.indexOf('&') >= 0) {
    decodeBox = decodeBox || document.createElement('textarea');
    decodeBox.innerHTML = id;
    id = decodeBox.value;
  }
  return id;
}
// 層を part に付ける。part.x などは初めて読んだときに DOM にする（svg 要素の innerHTML で読むと、丸ごと読んだときと同じ要素になる）。
// 文字列は part.src に残す（層があるか・中身の無いパーツかは、DOM にせず文字列で見る）
export function addLayer(part: Part, head: 'x' | 'a' | 'y', src: string) {
  let node: Element | null | undefined;
  part.src = part.src || {};
  part.src[head] = src;
  Object.defineProperty(part, head, { enumerable: true, configurable: true, get: () => {
    if (node === undefined) {
      parseBox = parseBox || document.createElementNS(NS, 'svg');
      parseBox.innerHTML = src;
      node = parseBox.firstElementChild;
      parseBox.textContent = '';
    }
    return node;
  } });
}
export function partsList(name: string): Part[] {
  if (partsLists[name]) return partsLists[name];
  const hash: Record<number, Part> = {}, list: Part[] = [];
  splitTop(C.partsCache[name].SVG).forEach((item) => {
    const id = topId(item);
    const head = id.substring(0, 1);
    let no: string | number = id.substring(1, 5).replace(/_/i, '');
    no = !isNaN(no as unknown as number) ? Number(no) : -9999;
    if (no < 0) return;
    if (!hash[no]) { hash[no] = { id: no }; list.push(hash[no]); }
    if (head === 'x' || head === 'a' || head === 'y') addLayer(hash[no], head, item);
  });
  list.sort((a, b) => a.id - b.id);
  partsLists[name] = list;
  return list;
}
// 層が1つでもあるか（part.x || part.a || part.y と同じ。DOM にしない）
export function hasLayers(part: Part) {
  return !!(part.src && (part.src.x || part.src.a || part.src.y));
}
// 中身の無いパーツ（たいてい先頭。押すとそのカテゴリのパーツを外す）か。前は層を DOM にして
// querySelector('path,rect,circle,ellipse,polygon,polyline,line,image,text') で見ていた。同じことを文字列で（自分の要素の中だけ）
export const DRAWN = /<(?:path|rect|circle|ellipse|polygon|polyline|line|image|text)[\s\/>]/;
export function partIsEmpty(cat: Category, part: Part) {
  return !layersOf(cat, part, true).some((s) => s && DRAWN.test(s.slice(s.indexOf('>') + 1)));
}
['pointerdown', 'keydown', 'wheel'].forEach((type) => {
  addEventListener(type, () => { lastInputAt = performance.now(); }, { capture: true, passive: true });
});
export const whenIdle: (fn: (deadline: { timeRemaining(): number }) => void) => unknown = window.requestIdleCallback || ((fn) => setTimeout(() => { fn({ timeRemaining: () => 8 }); }, 60));
// 起動のとき: キャラ（保存データ）に使うカテゴリと、はじめに開くカテゴリのパーツを、1カテゴリずつ別のタスクで読む
export function warmParts(save: SaveData | null, firstKey: string | null | undefined) {
  const names: string[] = [];
  const add = (key: string | null | undefined) => {
    const cat = catByKey[key as string];
    if (cat && C.partsCache[cat.NAME] && names.indexOf(cat.NAME) < 0) names.push(cat.NAME);
  };
  (save && save.c || []).forEach((line) => { add(String(line).split(',')[0]); });
  add(firstKey);
  return names.reduce<Promise<unknown>>((p, name) => p.then(() => { partsList(name); return later(); }), Promise.resolve());
}
export function prefetchParts() {
  var todo = C.cats.filter((c) => !partsLists[c.NAME] && C.partsCache[c.NAME]);
  function step(deadline: { timeRemaining(): number }) {
    if (performance.now() - lastInputAt < 1000) { setTimeout(() => { whenIdle(step); }, 500); return; }
    while (todo.length && partsLists[todo[0].NAME]) todo.shift(); // 開いたときに読んだもの
    if (todo.length && deadline.timeRemaining() > 4) partsList(todo.shift()!.NAME);
    if (todo.length) whenIdle(step);
  }
  whenIdle(step);
}
export function findPart(cat: Category, id: Num | null): Part | null {
  const no = Number(id);
  return partsList(cat.NAME).filter((p) => p.id === no)[0] || null;
}
// カテゴリのレイヤーごとに入れる層（TGLAYER が空ならどのレイヤーにも x。エンジンと同じ charat.draw.js の partLayers）
// asText: DOM にせず、元の文字列で返す（partIsEmpty）
export function layersOf(cat: Category, part: Part): (Element | null)[];
export function layersOf(cat: Category, part: Part, asText: true): (string | null)[];
export function layersOf(cat: Category, part: Part, asText?: boolean) {
  return charatDraw.partLayers<string | Element>(cat, asText ? (part.src || {}) : part);
}
